'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { aiModelsApi, IPAdapterGenerateResponse } from '@/lib/ai-models-api';

export default function IPAdapterPage() {
  const [referenceImage, setReferenceImage] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [adapterType, setAdapterType] = useState('plus');
  const [width, setWidth] = useState(1024);
  const [height, setHeight] = useState(1024);
  const [steps, setSteps] = useState(30);
  const [guidanceScale, setGuidanceScale] = useState(7.5);
  const [ipAdapterScale, setIpAdapterScale] = useState(0.6);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<IPAdapterGenerateResponse | null>(null);

  const handleImageChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.readAsDataURL(file);
    });

    setReferenceImage(base64);
    setImagePreview(URL.createObjectURL(file));
    setResult(null);
    setError(null);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!referenceImage) return;

    setLoading(true);
    setError(null);

    try {
      const response = await aiModelsApi.ipadapterGenerate(referenceImage, {
        prompt,
        negative_prompt: negativePrompt,
        adapter_type: adapterType,
        width,
        height,
        num_inference_steps: steps,
        guidance_scale: guidanceScale,
        ip_adapter_scale: ipAdapterScale,
      });

      setResult(response);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[var(--bg-primary)]">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-[var(--border-subtle)]">
        <div className="max-w-7xl mx-auto px-6 h-12 flex items-center gap-4">
          <Link
            href="/ai-models"
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </Link>
          <h1 className="text-lg font-semibold">IP-Adapter - Style Transfer</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: Parameters (2 cols) */}
            <div className="lg:col-span-2 space-y-6">
              <form onSubmit={handleSubmit} className="bg-[var(--bg-elevated)] rounded-xl p-6 space-y-5">
                <h2 className="text-lg font-semibold">Parameters</h2>

                {/* Reference Image Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">Reference Image</label>
                  <div className="relative">
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Reference"
                          className="w-full h-48 object-contain bg-[var(--bg-primary)] rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setReferenceImage(null);
                            setImagePreview(null);
                            setResult(null);
                          }}
                          className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-red-500 rounded transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-[var(--border-subtle)] rounded-lg cursor-pointer hover:border-[var(--accent)] transition-colors">
                        <svg className="w-10 h-10 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                        </svg>
                        <span className="mt-2 text-sm text-[var(--text-muted)]">Upload reference</span>
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                      </label>
                    )}
                  </div>
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    Generated image will follow the style/content of this reference
                  </p>
                </div>

                {/* Adapter Type */}
                <div>
                  <label className="block text-sm font-medium mb-2">Adapter Type</label>
                  <select
                    value={adapterType}
                    onChange={(e) => setAdapterType(e.target.value)}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  >
                    <option value="plus">Plus - Style & Content Transfer</option>
                    <option value="plus-face">Plus Face - Face Consistency</option>
                  </select>
                </div>

                {/* Prompt */}
                <div>
                  <label className="block text-sm font-medium mb-2">Prompt (Optional)</label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none h-20 resize-none"
                    placeholder="Add text guidance (optional)..."
                  />
                </div>

                {/* Negative Prompt */}
                <div>
                  <label className="block text-sm font-medium mb-2">Negative Prompt</label>
                  <input
                    type="text"
                    value={negativePrompt}
                    onChange={(e) => setNegativePrompt(e.target.value)}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                    placeholder="What to avoid..."
                  />
                </div>

                {/* Dimensions */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Width</label>
                    <select
                      value={width}
                      onChange={(e) => setWidth(parseInt(e.target.value))}
                      className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                    >
                      <option value={512}>512</option>
                      <option value={768}>768</option>
                      <option value={1024}>1024</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Height</label>
                    <select
                      value={height}
                      onChange={(e) => setHeight(parseInt(e.target.value))}
                      className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                    >
                      <option value={512}>512</option>
                      <option value={768}>768</option>
                      <option value={1024}>1024</option>
                    </select>
                  </div>
                </div>

                {/* Steps */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Steps: {steps}
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="50"
                    value={steps}
                    onChange={(e) => setSteps(parseInt(e.target.value))}
                    className="w-full accent-[var(--accent)]"
                  />
                </div>

                {/* Guidance Scale */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Text Guidance: {guidanceScale.toFixed(1)}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="15"
                    step="0.5"
                    value={guidanceScale}
                    onChange={(e) => setGuidanceScale(parseFloat(e.target.value))}
                    className="w-full accent-[var(--accent)]"
                  />
                </div>

                {/* IP-Adapter Scale */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Reference Strength: {ipAdapterScale.toFixed(2)}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1.2"
                    step="0.05"
                    value={ipAdapterScale}
                    onChange={(e) => setIpAdapterScale(parseFloat(e.target.value))}
                    className="w-full accent-[var(--accent)]"
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    Higher = more like reference, Lower = more creative freedom
                  </p>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!referenceImage || loading}
                  className="w-full py-3 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Generating...' : 'Generate'}
                </button>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}
              </form>
            </div>

            {/* Right: Results (3 cols) */}
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-[var(--bg-elevated)] rounded-xl p-6">
                <h2 className="text-lg font-semibold mb-4">Generated Image</h2>

                {!result && !loading && (
                  <div className="flex flex-col items-center justify-center py-48 text-center">
                    <svg className="w-16 h-16 text-[var(--text-muted)] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                    </svg>
                    <p className="text-[var(--text-muted)]">Upload a reference and generate an image</p>
                  </div>
                )}

                {loading && (
                  <div className="flex flex-col items-center justify-center py-48">
                    <div className="w-12 h-12 border-3 border-[var(--accent)] border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-[var(--text-muted)]">Generating image...</p>
                    <p className="text-xs text-[var(--text-muted)] mt-1">This may take a while</p>
                  </div>
                )}

                {result && (
                  <div className="space-y-4">
                    {/* Generated Image */}
                    <div>
                      <p className="text-sm text-[var(--text-muted)] mb-2">Generated Image</p>
                      <img
                        src={`data:image/png;base64,${result.image}`}
                        alt="Generated"
                        className="w-full rounded-lg"
                      />
                    </div>

                    {/* Download Button */}
                    <a
                      href={`data:image/png;base64,${result.image}`}
                      download="ipadapter_output.png"
                      className="block w-full py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors text-center"
                    >
                      Download Image
                    </a>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-[var(--bg-primary)] rounded-lg">
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Size</p>
                        <p className="text-lg font-semibold">{result.width}x{result.height}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Adapter</p>
                        <p className="text-lg font-semibold">{result.adapter_type}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Strength</p>
                        <p className="text-lg font-semibold">{result.ip_adapter_scale.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Time</p>
                        <p className="text-lg font-semibold">{(result.inference_time_ms / 1000).toFixed(1)}s</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
